import { orderBy } from 'lodash-es'

import { EDuration, EPaidProductType, EProductType } from '../type'
import { getProductType } from '../helpers/getProductType'

import type { $Enums } from '@prisma/client'
import type { TProductsList } from '../type'
import type { TUseSubLog } from '@/contexts/SiteSpecificContext/hooks/subLog'

import { ProductsSubUsageType } from '@/contexts/SiteSpecificContext/types'
import { useSiteSpecific } from '@/contexts/SiteSpecificContext'


import { useMemo } from 'react';
export interface IPlanProductListMap {
  gpt35Products: TProductsList
  duration: EDuration
  type: EProductType
}

function isUserGroupMatched({
  item,
  paidPlan,
  userGroup,
}: {
  item: TProductsList[number]
  paidPlan?: NonNullable<TUseSubLog['creditsPlan']>['productsSub']
  userGroup?: string
}) {
  if (!userGroup || !item.userGroup) {
    return true
  }

  const isCurrentPaidPlanMatched =
    paidPlan &&
    item.productName === paidPlan.productName &&
    item.usageType === paidPlan.usageType &&
    item.usageCount === paidPlan.usageCount

  if (isCurrentPaidPlanMatched) {
    // 命中“当前正在订阅的同档位商品”时，还要继续校验 productId，
    // 避免同名同额度但属于别的实验分桶的 SKU 混入当前列表。
    if (item.productId === paidPlan.productId) {
      return true
    }

    return false
  }

  const [start, end] = item.userGroup.split('-').map(Number)
  return start <= Number(userGroup) && Number(userGroup) <= end
}

export function filterProducts({
  list,
  appName,
  usageType,
  typeName,
  duration,
  userGroup,
  hasPrice,
  usagePlan,
}: {
  list: TProductsList
  appName?: $Enums.SubLogAppName
  usageType?: $Enums.UsageSubUsageType
  typeName?: EProductType | EPaidProductType
  duration?: EDuration
  userGroup?: string
  hasPrice?: boolean
  usagePlan?: TUseSubLog['creditsPlan']
}) {
  return list.filter((item) => {
    const productType = getProductType({ product: item })
    const paidPlan = usagePlan?.productsSub

    const flag1 = !appName || item.appName === appName
    const flag2 = !usageType || item.usageType === usageType
    const flag3 = !typeName || productType === typeName
    const flag4 = !duration || item.duration === duration
    const flag5 = isUserGroupMatched({
      item,
      paidPlan,
      userGroup,
    })
    const flag6 = hasPrice ? item.price > 0 : true

    return flag1 && flag2 && flag3 && flag4 && flag5 && flag6
  })
}

function createProductMaps({
  creditsProducts,
  productType,
  noProductType = false,
  userGroup,
  hasPrice = false,
  usagePlan,
}: {
  creditsProducts: TProductsList
  productType: EProductType | EPaidProductType
  /** 没有 productType 的默认全给 */
  noProductType?: boolean
  /** 价格大于0 */
  hasPrice?: boolean
  userGroup: string
  usagePlan?: TUseSubLog['creditsPlan']
}) {
  const monthProductMap = {
    creditsProducts: filterProducts({
      list: creditsProducts,
      duration: EDuration.MONTHLY,
      typeName: !noProductType ? productType : undefined,
      userGroup,
      hasPrice,
      usagePlan,
    }),
    duration: EDuration.MONTHLY,
    type: productType,
  }

  const yearProductMap = {
    creditsProducts: filterProducts({
      list: creditsProducts,
      duration: EDuration.YEARLY,
      typeName: !noProductType ? productType : undefined,
      userGroup,
      hasPrice,
      usagePlan,
    }),
    duration: EDuration.YEARLY,
    type: productType,
  }

  return {
    monthProductMap,
    yearProductMap,
    list: [monthProductMap, yearProductMap],
  }
}

export function usePlanProductList({
  initProductsList,
  appName,
  noProductType = false,
  hasPrice = false,
  effectiveUserGroup,
}: {
  initProductsList: TProductsList
  appName: $Enums.SubLogAppName
  /** 没有 productType 的则每个productType都给赋值 */
  noProductType?: boolean
  hasPrice?: boolean
  effectiveUserGroup?: string
}) {
  const { plan, user } = useSiteSpecific()
  const userGroup = effectiveUserGroup || user.userGroup

  const { creditsProducts } = useMemo(() => {
    const orderedList = orderBy(initProductsList, ['usageCount'])

    return {
      creditsProducts: filterProducts({
        list: orderedList,
        appName,
        usageType: ProductsSubUsageType.credits,
        userGroup,
        usagePlan: plan.creditsPlan,
      }),
    }
  }, [initProductsList, appName, plan.creditsPlan, userGroup])

  const {
    basicProductsMap,
    proProductsMap,
    unlimitedProductsMap,
    freeProductsMap,
    ultimateProductsMap,
  } = useMemo(() => {
    return {
      freeProductsMap: createProductMaps({
        creditsProducts,
        productType: EProductType.FREE,
        noProductType,
        userGroup,
        hasPrice,
        usagePlan: plan.creditsPlan,
      }),
      basicProductsMap: createProductMaps({
        creditsProducts,
        productType: EPaidProductType.BASIC,
        noProductType,
        userGroup,
        hasPrice,
        usagePlan: plan.creditsPlan,
      }),
      proProductsMap: createProductMaps({
        creditsProducts,
        productType: EPaidProductType.PRO,
        noProductType,
        userGroup,
        hasPrice,
        usagePlan: plan.creditsPlan,
      }),
      unlimitedProductsMap: createProductMaps({
        creditsProducts,
        productType: EPaidProductType.UNLIMITED,
        noProductType,
        userGroup,
        hasPrice,
        usagePlan: plan.creditsPlan,
      }),
      ultimateProductsMap: createProductMaps({
        creditsProducts,
        productType: EPaidProductType.ULTIMATE,
        noProductType,
        userGroup,
        hasPrice,
        usagePlan: plan.creditsPlan,
      }),
    }
  }, [creditsProducts, hasPrice, noProductType, plan.creditsPlan, userGroup])

  const allProductsList = useMemo(() => {
    // 默认套餐页按“卡片类型 + 计费周期”展开；
    // 这里只负责决定哪些类型会显示成卡片，不改变每张卡内部的具体 SKU 选择逻辑。
    return [
      ...basicProductsMap.list,
      ...unlimitedProductsMap.list,
      ...proProductsMap.list,
    ].filter((item) => item.creditsProducts.length > 0)
  }, [basicProductsMap.list, proProductsMap.list, unlimitedProductsMap.list])

  const allProductsListMap = useMemo(() => {
    return {
      [EProductType.BASIC]: basicProductsMap,
      [EProductType.PRO]: proProductsMap,
      [EProductType.UNLIMITED]: unlimitedProductsMap,
      [EProductType.FREE]: freeProductsMap,
      [EProductType.ULTIMATE]: ultimateProductsMap,
    }
  }, [
    basicProductsMap,
    freeProductsMap,
    proProductsMap,
    ultimateProductsMap,
    unlimitedProductsMap,
  ])

  return {
    creditsProducts,
    allProductsList,
    allProductsListMap,
  }
}

export type TUsePlanProductList = ReturnType<typeof usePlanProductList>
